/**
 * ChallengeInfoData Component
 * Builds and renders the challenge information data list
 * Handles both legacy and new goals configuration structures
 */

import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { c, t } from 'ttag'
import { List } from '@material-ui/core'
import {
  CustomType,
  DivisionCategoryId,
  DivisionCategoryRow,
  DivisionCategoryType,
  GoalWithProgress,
  Lang,
  ProgressType,
} from '../../../services/api.types'
import {
  getAvgSuffix,
  getChallengeGoalUnits,
  getChallengeProgressUnits,
  getDivisionCategoryIdFromLeaderboardId,
  GoalType,
  isDivisionCategoryLeaderboardId,
  isActivityIndexType,
  isCollectiveType,
  isPersistenceType,
  isSimpleDayType,
} from '../../../common/helpers/challenges'
import {
  formatThousands,
  getDailyGoalString,
  getDailyGoalStringLegacy,
  getProgressTypeString,
} from '../../../common/helpers/translations'
import { CustomTypeCategory, getActivityType, getCustomCategory } from '../../../services/CustomTypes'
import {
  CHALLENGE_ADMIN_STEP_CONFIGURATION,
  dailyGoalUnit,
  GoalConfig,
  GoalsConfig,
} from '../../ChallengeAdmin/ChallengeAdmin'
import { getDivisionCategoryIdsFromLeaderboards } from '../../../common/helpers/challenges'
import { PointsLogic } from '../../ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import { TotalPointsLogic } from '../../ChallengeAdmin/components/ChallengeTotalPoints/ChallengeTotalPoints'
import { formatDescription, formatDescriptionMarkdown } from '../../../common/helpers/activitiesHelpers'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'
import { getUserCompanies } from '../../../common/auth/authSelectors'
import { currentLang } from '../../../i18nInit'
import { translateDivisionCategoryType } from '../../TeamManagement/TeamManagement'
import { YumActivityIcon } from '../../../components/YumActivityIcon/YumActivityIcon'
import ChallengeInfoListItem from './ChallengeInfoListItem'
import PointsFormula from './PointsFormula'
import TotalPointsChallenges from './TotalPointsChallenges'
import { getPeriodWithDays, dailyMaxLabel } from '../ChallengeInfo.helpers'
import { ChallengeInfoListItemData } from '../ChallengeInfo.types'
import { ActivityView } from '../../Activity/store/ActivityReducer'

// Icons
import { ReactComponent as TitleIcon } from '../../../assets/v3/icons/challenge-info/title.svg'
import { ReactComponent as PeriodIcon } from '../../../assets/v3/icons/challenge-info/period.svg'
import { ReactComponent as PrizeIcon } from '../../../assets/v3/icons/challenge-info/prize.svg'
import { ReactComponent as DailyGoalIcon } from '../../../assets/v3/icons/challenge-info/daily-goal.svg'
import { ReactComponent as TotalGoalIcon } from '../../../assets/v3/icons/challenge-info/total-goal.svg'
import { ReactComponent as DescriptionIcon } from '../../../assets/v3/icons/challenge-info/description.svg'
import { ReactComponent as DailyMaxIcon } from '../../../assets/v3/icons/challenge-info/daily-max.svg'
import { isDailyMaxEnabled } from '../../ChallengeAdmin/components/ChallengeStepGoal/goalValidationHelpers'

interface ChallengeInfoDataProps {
  goal: GoalWithProgress
  goals: GoalWithProgress[]
  divisionCategories: DivisionCategoryRow[]
}

/**
 * Renders a goal section for a specific goal type (individual, team, group, country, collective)
 */
const renderGoalSection = (
  title: string,
  goalConfig: GoalConfig | null | undefined,
  goal: GoalWithProgress,
  isConsistencyChallenge: boolean,
  dontShowActivityType: boolean,
  category: CustomTypeCategory,
  useGoalValues: boolean = false,
  goalType: GoalType = 'individual',
): ChallengeInfoListItemData[] | null => {
  // Only render if goal config exists and is enabled
  if (!goalConfig || !goalConfig.enabled) {
    return null
  }

  const unit = getChallengeProgressUnits(goal)
  const goalContent: React.ReactNode[] = []
  const goalContent2: ChallengeInfoListItemData[] = []

  // For consistency challenges, show daily goal and persistence goal
  if (isConsistencyChallenge) {
    if (goalType === 'individual') {
      // let dailyGoalValue =
      //   typeof goalConfig.dailyGoal === 'number' && goalConfig.dailyGoal > 0 ? goalConfig.dailyGoal : null
      let dailyGoalValue = goal.dailyPersistenceGoal || null
      const isActivityIndex = isActivityIndexType(goal.progressType)
      if (isActivityIndex) dailyGoalValue = goalConfig?.indexTarget ? goalConfig.indexTarget : null

      // Don't do that here if possible:
      // if (dailyGoalValue && dailyGoalValue > 0) {
      //   let formattedGoal = formatThousands(dailyGoalValue) + ' ' + getChallengeGoalUnits(goal)
      //   if (isActivityIndex) formattedGoal = t`${dailyGoalValue}% of activity`
      //   goalContent2.push({
      //     label: t`Daily goal`,
      //     content: (
      //       <React.Fragment key="dailyGoal">
      //         {formattedGoal}
      //         <br />
      //       </React.Fragment>
      //     ),
      //     Icon: dontShowActivityType ? <YumActivityIcon category={category} bg={'gray'} /> : <DailyGoalIcon />,
      //   })
      // }
    }

    const persistenceGoalValue =
      typeof goalConfig.persistenceGoal === 'number' && goalConfig.persistenceGoal > 0
        ? goalConfig.persistenceGoal
        : null

    if (persistenceGoalValue && persistenceGoalValue > 0) {
      goalContent.push(
        <React.Fragment key="totalGoal">
          {currentLang === Lang.EN
            ? t`Achieve daily goal for ${persistenceGoalValue} days`
            : t`Achieve daily target for ${persistenceGoalValue} days`}
        </React.Fragment>,
      )
    }
  } else {
    // Get goal values - use goalConfig values if available, otherwise fallback to goal properties
    let goalTotalGoal = useGoalValues
      ? typeof goalConfig.totalGoal === 'number' && goalConfig.totalGoal > 0
        ? goalConfig.totalGoal
        : goal.stepsGoal || goal.customGoal || 0
      : goal.stepsGoal || goal.customGoal || 0
    const isActivityIndex = isActivityIndexType(goal.progressType)
    if (isActivityIndex) {
      goalTotalGoal = goalConfig.indexTarget ? goalConfig.indexTarget : goal.activityFormulaTarget || 0
    }

    // For total amount challenges, show total goal
    if (goalTotalGoal && typeof goalTotalGoal === 'number' && goalTotalGoal > 0) {
      // For group-based goals (team, country, group), show AVG suffix if avgAgg is enabled
      const avgText = getAvgSuffix(goalType, goal.avgAgg)
      const spacer = isActivityIndex ? '' : ' '
      goalContent.push(`${formatThousands(goalTotalGoal)}${spacer}${unit}${avgText}`)
    }
  }

  const result: ChallengeInfoListItemData[] = []

  if (goalContent2.length > 0) {
    result.push(...goalContent2)
  }

  if (goalContent.length > 0) {
    result.push({
      label: title,
      content: goalContent,
      //Icon: isConsistencyChallenge ? <DailyGoalIcon /> : <TotalGoalIcon />,
      Icon: <TotalGoalIcon />,
    })
  }

  // Don't render if no content
  if (result.length === 0) {
    return null
  }

  return result
}

/**
 * Builds legacy goal data (for challenges without goalsConfig)
 */
const buildLegacyGoalData = (
  goal: GoalWithProgress,
  isConsistencyChallenge: boolean,
  isCollectiveChallenge: boolean,
  dontShowActivityType: boolean,
  category: CustomTypeCategory,
): ChallengeInfoListItemData[] => {
  const data: ChallengeInfoListItemData[] = []

  if (!isConsistencyChallenge) {
    data.push({
      label: isCollectiveChallenge ? t`Collective goal` : t`Goal`,
      content: getProgressTypeString(goal, true),
      Icon: <TotalGoalIcon />,
    })
  }

  if (isConsistencyChallenge) {
    data.push({
      label: t`Daily goal`,
      content: <>{getDailyGoalStringLegacy(goal)}</>,
      Icon: dontShowActivityType ? <YumActivityIcon category={category} bg={'gray'} /> : <DailyGoalIcon />,
    })

    data.push({
      label: t`Total goal`,
      content:
        currentLang === Lang.EN
          ? t`Achieve daily goal for ${goal.persistenceGoal} days`
          : t`Achieve daily target for ${goal.persistenceGoal} days`,
      Icon: <TotalGoalIcon />,
    })
  }

  return data
}

/**
 * Builds a translated "{group} goal" label for division categories.
 * We use explicit strings per category so translations can target each label.
 */
const getDivisionCategoryGoalTitle = (category: DivisionCategoryRow): string => {
  // Keep category-specific strings static for clean translation extraction.
  switch (category.type) {
    case DivisionCategoryType.AREA:
      return t`Area goal`
    case DivisionCategoryType.CLUB:
      return t`Club goal`
    case DivisionCategoryType.DEPARTMENT:
      return t`Department goal`
    case DivisionCategoryType.DIVISION:
      return t`Division goal`
    case DivisionCategoryType.LOCATION:
      return t`Location goal`
    case DivisionCategoryType.REGION:
      return t`Region goal`
    case DivisionCategoryType.STATE:
      return c('State as a nation or territory considered as an organized political community under one government.')
        .t`State goal`
    case DivisionCategoryType.CUSTOM: {
      // Custom names are user-defined, so we append a translated "goal".
      const customName = category.customName || ''
      return customName ? `${customName} ${t`goal`}` : t`Custom goal`
    }
    default:
      // Fallback to generic "goal" if we receive an unknown type.
      return t`Goal`
  }
}

/**
 * Parses a leaderboards value into a normalized ordered list.
 * Keeps the raw order intact so UI ordering matches backend config.
 */
const parseLeaderboardsRaw = (value: unknown): string[] => {
  // Keep parsing rules tiny and defensive to avoid breaking older data.
  if (Array.isArray(value)) return value as string[]
  if (!value) return []
  try {
    return JSON.parse(value as any) as string[]
  } catch (e) {
    return []
  }
}

/**
 * Resolves a single leaderboard ID into a division category ID (if applicable).
 * Supports both new prefixed IDs and legacy raw category IDs.
 */
const resolveDivisionCategoryId = (leaderboardId: string, divisionCategories: DivisionCategoryRow[]): string | null => {
  // New format: "DIVISION_CATEGORY_*" IDs.
  if (isDivisionCategoryLeaderboardId(leaderboardId)) {
    return getDivisionCategoryIdFromLeaderboardId(leaderboardId)
  }
  // Legacy format: raw category IDs stored directly in leaderboards.
  const hasLegacyMatch = divisionCategories.some((category) => category.id === leaderboardId)
  return hasLegacyMatch ? leaderboardId : null
}

/**
 * Builds goal data from goalsConfig (new structure)
 */
const buildGoalsConfigData = (
  goalsConfig: GoalsConfig,
  goal: GoalWithProgress,
  divisionCategories: DivisionCategoryRow[],
  isConsistencyChallenge: boolean,
  dontShowActivityType: boolean,
  category: CustomTypeCategory,
  isCalories: boolean,
): ChallengeInfoListItemData[] => {
  const data: ChallengeInfoListItemData[] = []
  // Default config keeps the helper calls safe even when individual goal is missing.
  const individualGoalConfig: GoalConfig = goalsConfig.individual || {
    enabled: false,
    totalGoal: '',
    //dailyGoal: '',
    persistenceGoal: '',
    indexTarget: '',
  }

  // Only show the daily goal when at least one goal is enabled.
  const hasEnabledGoal =
    !!goalsConfig.individual?.enabled ||
    !!goalsConfig.collective?.enabled ||
    !!goalsConfig.team?.enabled ||
    !!goalsConfig.country?.enabled ||
    Object.values(goalsConfig.group || {}).some((groupGoal) => !!groupGoal?.enabled)

  if (isConsistencyChallenge && hasEnabledGoal /*&& (isCalories || goal.hidePeopleTab)*/) {
    data.push({
      label: t`Daily goal`,
      content: <>{getDailyGoalString(goal, individualGoalConfig)}</>,
      Icon: dontShowActivityType ? <YumActivityIcon category={category} bg={'gray'} /> : <DailyGoalIcon />,
    })
  }

  // Goal section builders. We keep them tiny and reusable for ordered rendering.
  const pushIndividualGoal = () => {
    // Individual goals (only if not calories activity type)
    if (!isCalories && !goal.hidePeopleTab) {
      const individualGoal = renderGoalSection(
        t`Individual goal`,
        individualGoalConfig,
        goal,
        isConsistencyChallenge,
        dontShowActivityType,
        category,
        !!goalsConfig.individual,
        'individual',
      )
      if (individualGoal) data.push(...individualGoal)
    }
  }

  const pushTeamGoal = () => {
    // Team goals
    if (!isCalories && goalsConfig.team && !goal.hideGroupsTab) {
      const teamGoal = renderGoalSection(
        t`Team goal`,
        goalsConfig.team,
        goal,
        isConsistencyChallenge,
        dontShowActivityType,
        category,
        true,
        'team',
      )
      if (teamGoal) data.push(...teamGoal)
    }
  }

  const pushGroupGoalByCategoryId = (categoryId: string) => {
    // Custom group goals (division categories) must match the leaderboards ordering.
    if (!isCalories && goalsConfig.group) {
      const groupGoal = goalsConfig.group ? goalsConfig.group[categoryId] : null
      if (!groupGoal || !groupGoal.enabled) return

      const categoryMatch = divisionCategories.find((c) => c.id === categoryId)
      if (!categoryMatch) return

      // Use translated "{group} goal" label (e.g. "Region goal").
      const groupTitle = getDivisionCategoryGoalTitle(categoryMatch)
      const renderedGoal = renderGoalSection(
        groupTitle,
        groupGoal,
        goal,
        isConsistencyChallenge,
        dontShowActivityType,
        category,
        true,
        'group',
      )
      if (renderedGoal) data.push(...renderedGoal)
    }
  }

  const pushCountryGoal = () => {
    // Country goals
    if (!isCalories && goalsConfig.country && !goal.hideCountriesTab) {
      const countryGoal = renderGoalSection(
        t`Country goal`,
        goalsConfig.country,
        goal,
        isConsistencyChallenge,
        dontShowActivityType,
        category,
        true,
        'country',
      )
      if (countryGoal) data.push(...countryGoal)
    }
  }

  const pushCollectiveGoal = () => {
    // Collective goals (only if not ALL activity type)
    if (category !== CustomTypeCategory.ALL && goalsConfig.collective) {
      const collectiveGoal = renderGoalSection(
        t`Collective goal`,
        goalsConfig.collective,
        goal,
        isConsistencyChallenge,
        dontShowActivityType,
        category,
        true,
        'collective',
      )
      if (collectiveGoal) data.push(...collectiveGoal)
    }
  }

  // Use the leaderboards array order when available.
  const leaderboardsOrdered = parseLeaderboardsRaw(goal.leaderboards)
  const shouldUseLeaderboardsOrder = leaderboardsOrdered.length > 0

  if (shouldUseLeaderboardsOrder) {
    // Render goal sections in the same order as the leaderboards array.
    leaderboardsOrdered.forEach((leaderboardId) => {
      if (leaderboardId === ActivityView.INDIVIDUAL) {
        pushIndividualGoal()
        return
      }
      if (leaderboardId === ActivityView.GROUP) {
        pushTeamGoal()
        return
      }
      if (leaderboardId === ActivityView.COUNTRY) {
        pushCountryGoal()
        return
      }
      if (leaderboardId === ActivityView.COLLECTIVE) {
        pushCollectiveGoal()
        return
      }

      // Division category leaderboards resolve to custom group goals.
      const divisionCategoryId = resolveDivisionCategoryId(leaderboardId, divisionCategories)
      if (divisionCategoryId) {
        pushGroupGoalByCategoryId(divisionCategoryId)
      }
    })
  } else {
    // Fallback to the historical fixed order when leaderboards is missing.
    pushIndividualGoal()
    pushTeamGoal()
    // Keep legacy group ordering based on leaderboards when available.
    if (!isCalories && goalsConfig.group && goal.leaderboards && goal.leaderboards.length > 0) {
      // Parse and normalize the leaderboards list.
      const leaderboardsRaw = parseLeaderboardsRaw(goal.leaderboards)
      const leaderboards = getDivisionCategoryIdsFromLeaderboards(leaderboardsRaw)

      // Render custom group goals in the same order as the leaderboards list.
      leaderboards.forEach((categoryId) => {
        pushGroupGoalByCategoryId(categoryId)
      })
    }
    pushCountryGoal()
    pushCollectiveGoal()
  }

  return data
}

/**
 * ChallengeInfoData builds and renders the full challenge information list
 */
const ChallengeInfoData: React.FC<ChallengeInfoDataProps> = ({ goal, goals, divisionCategories }) => {
  const userCompanies = useSelector(getUserCompanies)
  const useMarkdown = hasFeatureFlag(userCompanies, 'challengeDescriptionMarkdown')

  // Memoize the challenge info data
  const data = useMemo(() => {
    const { title, description, progressType, voucher, roundDailyPoints } = goal
    const items: ChallengeInfoListItemData[] = []

    // Challenge type flags
    const isConsistencyChallenge = isPersistenceType(progressType)
    const isCollectiveChallenge = isCollectiveType(progressType)
    const isPoints = goal.customType === CustomType.POINTS || goal.customType === CustomType.TOTAL_POINTS
    const category = getCustomCategory(progressType, goal.customType)
    const isCalories = category === CustomTypeCategory.CALORIES

    // Don't show activity type if shown under daily goal
    const dontShowActivityType =
      progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY || (isSimpleDayType(category) && isConsistencyChallenge)

    // Title
    if (title) {
      items.push({
        label: t`Challenge name`,
        content: title,
        Icon: <TitleIcon />,
      })
    }

    // Period
    items.push({
      label: t`Period`,
      content: getPeriodWithDays(goal),
      Icon: <PeriodIcon />,
    })

    // Activity type (if not shown elsewhere)
    if (!dontShowActivityType) {
      const pointsLogic = (goal.customType === CustomType.POINTS ? JSON.parse(goal.pointsLogic) : []) as PointsLogic[]
      const totalPointsLogic = (
        goal.customType === CustomType.TOTAL_POINTS ? JSON.parse(goal.pointsLogic) : []
      ) as TotalPointsLogic[]

      items.push({
        label: t`Activity type`,
        content: (
          <>
            {getActivityType(goal)}
            <PointsFormula pointsLogic={pointsLogic} roundDailyPoints={roundDailyPoints} />
            <TotalPointsChallenges totalPointsLogic={totalPointsLogic} goals={goals} />
          </>
        ),
        Icon: <YumActivityIcon category={category} bg={'gray'} />,
      })
    }

    // Goals section
    const goalsConfig = JSON.parse(goal.goals) as GoalsConfig | null | undefined

    if (!goalsConfig) {
      // Legacy logic: use existing goal properties
      items.push(
        ...buildLegacyGoalData(goal, isConsistencyChallenge, isCollectiveChallenge, dontShowActivityType, category),
      )
    } else {
      // New logic: use goalsConfig structure
      items.push(
        ...buildGoalsConfigData(
          goalsConfig,
          goal,
          divisionCategories,
          isConsistencyChallenge,
          dontShowActivityType,
          category,
          isCalories,
        ),
      )
    }

    // Daily max (as separate block after goals)
    const goalType = isConsistencyChallenge ? 'consistentDays' : 'totalAmount'
    const dailyMaxSupported = isDailyMaxEnabled(
      CHALLENGE_ADMIN_STEP_CONFIGURATION,
      category,
      goalType,
      goal.simpleManualEntry,
    )
    const dailyMax = goal.dailyMax
    const showDailyMax = dailyMaxSupported && dailyMax && dailyMax > 0

    if (showDailyMax) {
      items.push({
        label: dailyMaxLabel,
        content: `${formatThousands(dailyMax)} ${dailyGoalUnit(goal.progressType, goal.customType, goal.customUnit)}`,
        // TODO: Add icon for daily max
        Icon: <DailyMaxIcon />,
      })
    }

    // Prize
    if (voucher) {
      items.push({
        label: t`Prize`,
        content: <span style={{ whiteSpace: 'pre-line' }}>{formatDescription(voucher)}</span>,
        Icon: <PrizeIcon />,
      })
    }

    // Description
    if (description) {
      items.push({
        label: t`Description`,
        content: useMarkdown ? (
          formatDescriptionMarkdown(description)
        ) : (
          <span style={{ whiteSpace: 'pre-line' }}>{formatDescription(description)}</span>
        ),
        Icon: <DescriptionIcon />,
      })
    }

    return items
  }, [goal, goals, divisionCategories, useMarkdown])

  return (
    <List>
      {data.map((itemData, index) => (
        <ChallengeInfoListItem key={index} data={itemData} index={index} />
      ))}
    </List>
  )
}

export default ChallengeInfoData
